import { forEach } from 'lodash-es'
import {
  type MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import {
  getTableSearchCache,
  setTableSearchCacheValue,
  TABLE_QUERY_RESERVED_KEYS,
  type TableSearchValues,
} from '../utils/table-search-cache'

interface TableParams {
  defaultSortBy?: string | undefined
  defaultSize?: number
  defaultPage?: number
  extraParams?: { [key: string]: string }
  onlyExtraParams?: boolean
}

const compactExtraParams = (
  values: TableSearchValues,
): { [key: string]: string } => {
  const next: { [key: string]: string } = {}
  Object.entries(values).forEach(([key, value]) => {
    if (value) {
      next[key] = String(value)
    }
  })
  return next
}

const readExtraParamsFromUrl = (
  searchParams: URLSearchParams,
  declared?: { [key: string]: string },
  cached?: TableSearchValues,
) => {
  const next: { [key: string]: string } = {}

  forEach(declared, (defaultValue, key) => {
    next[key] =
      searchParams.get(key) || (cached?.[key] as string) || defaultValue || ''
  })

  Object.entries(cached ?? {}).forEach(([key, value]) => {
    if (!TABLE_QUERY_RESERVED_KEYS.has(key) && value && !next[key]) {
      next[key] = String(value)
    }
  })

  searchParams.forEach((value, key) => {
    if (!TABLE_QUERY_RESERVED_KEYS.has(key) && value) {
      next[key] = value
    }
  })

  return next
}

export const useTableParams = (params?: TableParams) => {
  const { pathname } = useLocation()
  const [urlSearchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(
    Number(urlSearchParams.get('page')) || params?.defaultPage || 1,
  )
  const [size, setSize] = useState(
    Number(urlSearchParams.get('size')) || params?.defaultSize || 10,
  )
  const [sortBy, setSortBy] = useState<string | undefined>(
    urlSearchParams.get('sortBy') || params?.defaultSortBy || undefined,
  )

  const [extraParams, setExtraParams] = useState<{ [key: string]: string }>(
    () =>
      readExtraParamsFromUrl(
        urlSearchParams,
        params?.extraParams,
        getTableSearchCache(pathname),
      ),
  )

  const extraParamsRef = useRef(extraParams)
  extraParamsRef.current = extraParams

  const searchParams = useMemo(() => {
    const ref = {
      get current() {
        return extraParamsRef.current as TableSearchValues
      },
      set current(value: TableSearchValues) {
        const cleaned = compactExtraParams(value)
        if (extraParamsRef.current.key && !cleaned.key) {
          cleaned.key = extraParamsRef.current.key
        }
        extraParamsRef.current = cleaned
        Object.entries(value).forEach(([key, val]) => {
          if (!TABLE_QUERY_RESERVED_KEYS.has(key)) {
            setTableSearchCacheValue(pathname, key, val ? String(val) : null)
          }
        })
        setExtraParams(cleaned)
      },
    }
    return ref as MutableRefObject<TableSearchValues>
  }, [pathname])

  const [totalElements, setTotalElemenets] = useState<number>(10)

  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (params?.onlyExtraParams) {
          const next = new URLSearchParams()
          Object.entries(extraParams).forEach(([key, value]) => {
            if (value) {
              next.set(key, value)
            }
          })
          return next
        }

        const next = new URLSearchParams(prev)
        next.set('page', page.toString())
        next.set('size', size.toString())
        if (sortBy) {
          next.set('sortBy', sortBy)
        } else {
          next.delete('sortBy')
        }
        Object.entries(extraParams).forEach(([key, value]) => {
          if (value) {
            next.set(key, value)
          } else {
            next.delete(key)
          }
        })
        return next
      },
      { replace: true },
    )
  }, [
    page,
    sortBy,
    size,
    extraParams,
    params?.onlyExtraParams,
    setSearchParams,
  ])

  return {
    page: page - 1,
    setPage,
    size,
    setSize,
    sortBy,
    setSortBy,
    totalElements,
    setTotalElemenets,
    extraParams,
    setExtraParams,
    searchParams,
  }
}
